// src/hooks/useRecruitment.js
// All Firestore hooks for the Recruitment ATS module

import { useState, useEffect } from "react";
import {
  collection, doc, query, where, orderBy, limit,
  onSnapshot, addDoc, updateDoc, deleteDoc,
  serverTimestamp, getDoc,
} from "firebase/firestore";
import { db } from "../firebase/config";
import { sendInterviewInvitationEmail } from "../utils/emailService";

// ─── Utility: log an activity entry ───────────────────────────────────────
export async function logRecruitmentActivity({
  actorId, actorName, actorRole,
  action, entityType, entityId, entityName,
  oldValue = null, newValue = null,
  candidateId = null, jobId = null,
}) {
  try {
    await addDoc(collection(db, "recruitmentActivity"), {
      actorId, actorName, actorRole,
      action, entityType, entityId, entityName,
      oldValue, newValue,
      candidateId, jobId,
      createdAt: serverTimestamp(),
    });
  } catch (e) {
    console.error("[recruitmentActivity] log failed:", e);
  }
}

// ─── Job Openings ──────────────────────────────────────────────────────────
export function useJobOpenings(filters = {}) {
  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let q = query(collection(db, "jobOpenings"), orderBy("createdAt", "desc"));
    if (filters.status) q = query(collection(db, "jobOpenings"), where("status", "==", filters.status), orderBy("createdAt", "desc"));
    const unsub = onSnapshot(q, (snap) => {
      setData(snap.docs.map((d) => ({ id: d.id, ...d.data() })));
      setLoading(false);
    }, (e) => { console.error("[useJobOpenings]", e); setLoading(false); });
    return unsub;
  }, [filters.status]);

  return { data, loading };
}

export async function createJobOpening(data) {
  const ref = await addDoc(collection(db, "jobOpenings"), {
    ...data,
    applicantCount: 0,
    status: data.status || "Open",
    createdAt: serverTimestamp(),
    updatedAt: serverTimestamp(),
  });
  return ref.id;
}

export async function updateJobOpening(id, data) {
  await updateDoc(doc(db, "jobOpenings", id), { ...data, updatedAt: serverTimestamp() });
}

export async function deleteJobOpening(id) {
  await deleteDoc(doc(db, "jobOpenings", id));
}

// ─── Candidates ────────────────────────────────────────────────────────────
export function useCandidates(jobId = null) {
  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let q = jobId
      ? query(collection(db, "candidates"), where("jobId", "==", jobId), orderBy("createdAt", "desc"))
      : query(collection(db, "candidates"), orderBy("createdAt", "desc"));
    const unsub = onSnapshot(q, (snap) => {
      setData(snap.docs.map((d) => ({ id: d.id, ...d.data() })));
      setLoading(false);
    }, (e) => { console.error("[useCandidates]", e); setLoading(false); });
    return unsub;
  }, [jobId]);

  return { data, loading };
}

export async function createCandidate(data) {
  const ref = await addDoc(collection(db, "candidates"), {
    ...data,
    stage: data.stage || "Applied",
    leadRecommendation: data.leadRecommendation || "Pending",
    tags: data.tags || [],
    createdAt: serverTimestamp(),
    updatedAt: serverTimestamp(),
    stageHistory: [{ stage: data.stage || "Applied", movedAt: new Date().toISOString(), movedBy: data.createdByName || "System" }],
  });
  // Increment applicant count on job opening if associated
  if (data.jobId) {
    try {
      const { increment } = await import("firebase/firestore");
      await updateDoc(doc(db, "jobOpenings", data.jobId), { applicantCount: increment(1) });
    } catch (_) {}
  }
  return ref.id;
}

export async function deleteCandidate(id, jobId = null) {
  await deleteDoc(doc(db, "candidates", id));
  if (jobId) {
    try {
      const { increment } = await import("firebase/firestore");
      await updateDoc(doc(db, "jobOpenings", jobId), { applicantCount: increment(-1) });
    } catch (_) {}
  }
}

export async function updateCandidateStage(candidateId, newStage, actorName, jobId) {
  const ref = doc(db, "candidates", candidateId);
  const snap = await getDoc(ref);
  const oldStage = snap.data()?.stage || "—";
  const stageHistory = snap.data()?.stageHistory || [];
  stageHistory.push({ stage: newStage, movedAt: new Date().toISOString(), movedBy: actorName });
  await updateDoc(ref, {
    stage: newStage,
    stageHistory,
    updatedAt: serverTimestamp(),
  });
  await logRecruitmentActivity({
    actorId: "", actorName, actorRole: "hr",
    action: "STAGE_MOVED", entityType: "candidate", entityId: candidateId,
    entityName: snap.data()?.name || candidateId,
    oldValue: oldStage, newValue: newStage, candidateId, jobId,
  });
}

export async function updateCandidate(id, data) {
  await updateDoc(doc(db, "candidates", id), { ...data, updatedAt: serverTimestamp() });
}

// ─── HR Resume Screening Helpers ──────────────────────────────────────────
export async function shortlistCandidate(candidateId, hrNotes = "", actorName = "HR Admin") {
  const ref = doc(db, "candidates", candidateId);
  const snap = await getDoc(ref);
  await updateDoc(ref, {
    stage: "Shortlisted",
    hrScreened: true,
    hrScreenedBy: actorName,
    hrScreenedAt: new Date().toISOString(),
    hrScreeningNotes: hrNotes,
    updatedAt: serverTimestamp(),
  });
  await logRecruitmentActivity({
    actorId: "", actorName, actorRole: "hr",
    action: "CANDIDATE_SHORTLISTED", entityType: "candidate", entityId: candidateId,
    entityName: snap.data()?.name || candidateId,
    newValue: "Shortlisted by HR",
    candidateId,
  });
}

export async function holdCandidate(candidateId, hrNotes = "", actorName = "HR Admin") {
  const ref = doc(db, "candidates", candidateId);
  const snap = await getDoc(ref);
  await updateDoc(ref, {
    stage: "Hold",
    hrScreened: true,
    hrScreenedBy: actorName,
    hrScreenedAt: new Date().toISOString(),
    hrScreeningNotes: hrNotes,
    updatedAt: serverTimestamp(),
  });
  await logRecruitmentActivity({
    actorId: "", actorName, actorRole: "hr",
    action: "CANDIDATE_ON_HOLD", entityType: "candidate", entityId: candidateId,
    entityName: snap.data()?.name || candidateId,
    newValue: "Put on Hold by HR",
    candidateId,
  });
}

export async function rejectCandidate(candidateId, reason = "", actorName = "HR Admin") {
  const ref = doc(db, "candidates", candidateId);
  const snap = await getDoc(ref);
  await updateDoc(ref, {
    stage: "Rejected",
    hrScreened: true,
    hrScreenedBy: actorName,
    hrScreenedAt: new Date().toISOString(),
    hrScreeningNotes: reason,
    updatedAt: serverTimestamp(),
  });
  await logRecruitmentActivity({
    actorId: "", actorName, actorRole: "hr",
    action: "CANDIDATE_REJECTED", entityType: "candidate", entityId: candidateId,
    entityName: snap.data()?.name || candidateId,
    newValue: `Rejected by HR: ${reason}`,
    candidateId,
  });
}

export async function updateCandidateScreeningNotes(candidateId, hrNotes = "", actorName = "HR Admin") {
  const ref = doc(db, "candidates", candidateId);
  await updateDoc(ref, {
    hrScreeningNotes: hrNotes,
    updatedAt: serverTimestamp(),
  });
}

// ─── Team Lead Screening & Recommendation Helpers ─────────────────────────
export async function assignCandidateToLead(candidateId, leadId, leadName, hrUserName = "HR Admin") {
  const ref = doc(db, "candidates", candidateId);
  const snap = await getDoc(ref);
  const candName = snap.data()?.name || candidateId;

  await updateDoc(ref, {
    assignedLeadId: leadId,
    assignedLeadName: leadName,
    stage: "Screening",
    leadRecommendation: "Pending Review",
    assignedAt: new Date().toISOString(),
    updatedAt: serverTimestamp(),
  });

  await logRecruitmentActivity({
    actorId: "", actorName: hrUserName, actorRole: "hr",
    action: "ASSIGNED_TO_LEAD", entityType: "candidate", entityId: candidateId,
    entityName: candName,
    newValue: `Assigned to Lead: ${leadName}`,
    candidateId,
  });
}

export async function submitLeadRecommendation(candidateId, recommendation, comments = "", leadUserName = "Team Lead", technicalRating = 8) {
  const ref = doc(db, "candidates", candidateId);
  const snap = await getDoc(ref);
  const candName = snap.data()?.name || candidateId;

  await updateDoc(ref, {
    leadRecommendation: recommendation, // "Proceed" | "Proceed to Next Round" | "Hold" | "Reject"
    technicalRating: Number(technicalRating) || 8,
    technicalFeedback: comments,
    leadComments: comments,
    leadReviewedBy: leadUserName,
    leadReviewedAt: new Date().toISOString(),
    stage: "Pending Admin Review",
    adminAssigned: true,
    adminAssignedAt: new Date().toISOString(),
    status: "In Review",
    updatedAt: serverTimestamp(),
  });

  await logRecruitmentActivity({
    actorId: "", actorName: leadUserName, actorRole: "lead",
    action: "TECHNICAL_EVALUATION_SUBMITTED", entityType: "candidate", entityId: candidateId,
    entityName: candName,
    oldValue: snap.data()?.stage || "Assigned to Team Lead",
    newValue: `Recommendation: ${recommendation} | Rating: ${technicalRating}/10 | Stage: Pending Admin Review`,
    candidateId,
  });

  try {
    await addDoc(collection(db, "notifications"), {
      recipientId: "admin",
      recipientRole: "admin",
      title: "New Technical Evaluation Submitted",
      message: `Team Lead ${leadUserName} submitted technical evaluation for candidate ${candName} (Rating: ${technicalRating}/10, Decision: ${recommendation}). Candidate is assigned to Admin for final decision.`,
      link: "/admin/assigned-candidates",
      read: false,
      createdAt: serverTimestamp(),
    });
  } catch (e) {
    console.error("[submitLeadRecommendation] Notification error:", e);
  }
}

export async function submitAdminCandidateDecision(candidateId, { adminDecision, adminRemarks = "", adminName = "Admin", adminId = "" }) {
  const ref = doc(db, "candidates", candidateId);
  const snap = await getDoc(ref);
  const candidateData = snap.data() || {};
  const candName = candidateData.name || candidateId;
  const oldStage = candidateData.stage || "Pending Admin Review";

  let newStage = "Pending HR Final Review";
  let newStatus = "Pending HR Review";
  let isArchived = false;

  if (adminDecision === "Proceed to Next Round" || adminDecision === "Proceed" || adminDecision === "Move to Onboarding") {
    newStage = "Pending HR Final Review";
    newStatus = "Pending HR Review";
  } else if (adminDecision === "On Hold") {
    newStage = "On Hold";
    newStatus = "On Hold";
  } else if (adminDecision === "Reject") {
    newStage = "Rejected";
    newStatus = "Rejected";
    isArchived = true;
  }

  const stageHistory = Array.isArray(candidateData.stageHistory) ? [...candidateData.stageHistory] : [];
  stageHistory.push({
    stage: newStage,
    movedAt: new Date().toISOString(),
    movedBy: adminName,
    remarks: `Admin Decision: ${adminDecision}${adminRemarks ? ` - ${adminRemarks}` : ""}`,
  });

  const updatePayload = {
    adminDecision,
    adminRemarks,
    adminReviewedBy: adminName,
    adminReviewedId: adminId,
    adminReviewedAt: new Date().toISOString(),
    stage: newStage,
    status: newStatus,
    archived: isArchived,
    stageHistory,
    updatedAt: serverTimestamp(),
  };

  await updateDoc(ref, updatePayload);

  await logRecruitmentActivity({
    actorId: adminId,
    actorName: adminName,
    actorRole: "admin",
    action: "ADMIN_FINAL_DECISION",
    entityType: "candidate",
    entityId: candidateId,
    entityName: candName,
    oldValue: oldStage,
    newValue: `Admin Decision: ${adminDecision} | Stage: ${newStage}`,
    candidateId,
  });

  // Notify HR that candidate is ready for HR Final Action (Move to Onboarding)
  if (adminDecision === "Proceed to Next Round" || adminDecision === "Proceed" || adminDecision === "Move to Onboarding") {
    try {
      await addDoc(collection(db, "notifications"), {
        recipientId: "hr",
        recipientRole: "hr",
        title: "Candidate Approved by Admin",
        message: `Admin ${adminName} approved candidate ${candName} (${candidateData.appliedPosition || "Position"}). Candidate is now pending HR Final Review to move to Onboarding.`,
        link: "/hr/assigned-candidates",
        read: false,
        createdAt: serverTimestamp(),
      });
    } catch (_) {}
  } else if (adminDecision === "Reject") {
    try {
      await addDoc(collection(db, "notifications"), {
        recipientId: "hr",
        recipientRole: "hr",
        title: "Candidate Rejected by Admin",
        message: `Admin ${adminName} marked candidate ${candName} as Rejected. Record archived.`,
        link: "/hr/recruitment",
        read: false,
        createdAt: serverTimestamp(),
      });
    } catch (_) {}
  }

  // Notify Team Lead
  if (candidateData.assignedLeadId) {
    try {
      await addDoc(collection(db, "notifications"), {
        recipientId: candidateData.assignedLeadId,
        recipientRole: "lead",
        title: "Admin Review Completed",
        message: `Admin ${adminName} finalized decision "${adminDecision}" for candidate ${candName}.`,
        link: "/lead/recruitment",
        read: false,
        createdAt: serverTimestamp(),
      });
    } catch (_) {}
  }
}

// ─── HR Final Recruitment Decision Helper ─────────────────────────────────
export async function submitHRFinalDecision(candidateId, { hrAction, hrRemarks = "", hrName = "HR Admin", hrId = "", scheduledDate = "", scheduledTime = "", meetingLink = "", offerDetails = null }) {
  const ref = doc(db, "candidates", candidateId);
  const snap = await getDoc(ref);
  const candidateData = snap.data() || {};
  const candName = candidateData.name || candidateId;
  const oldStage = candidateData.stage || "Pending HR Final Review";

  let newStage = hrAction;
  let newStatus = "In Progress";

  if (hrAction === "Proceed to Next Round") {
    newStage = "Proceeded to Next Round";
    newStatus = "In Progress";
  } else if (hrAction === "Schedule HR Interview") {
    newStage = "HR Interview Scheduled";
    newStatus = "HR Interview";
  } else if (hrAction === "On Hold") {
    newStage = "On Hold";
    newStatus = "Hold";
  } else if (hrAction === "Reject") {
    newStage = "Rejected";
    newStatus = "Rejected";
  } else if (hrAction === "Generate Offer Letter") {
    newStage = "Offer Letter Generated";
    newStatus = "Offer Generated";
  } else if (hrAction === "Send Offer") {
    newStage = "Offer Sent";
    newStatus = "Offer Sent";
  } else if (hrAction === "Move to Onboarding") {
    newStage = "Moved to Onboarding";
    newStatus = "Onboarding";
  } else if (hrAction === "Mark as Hired") {
    newStage = "Hired";
    newStatus = "Hired";
  }

  const stageHistory = Array.isArray(candidateData.stageHistory) ? [...candidateData.stageHistory] : [];
  stageHistory.push({
    stage: newStage,
    movedAt: new Date().toISOString(),
    movedBy: hrName,
    remarks: hrRemarks,
  });

  const updatePayload = {
    hrFinalAction: hrAction,
    hrFinalRemarks: hrRemarks,
    hrFinalizedBy: hrName,
    hrFinalizedId: hrId,
    hrFinalizedAt: new Date().toISOString(),
    stage: newStage,
    status: newStatus,
    stageHistory,
    updatedAt: serverTimestamp(),
  };

  if (hrAction === "Move to Onboarding" || hrAction === "Mark as Hired" || newStage === "Moved to Onboarding") {
    updatePayload.onboarding = {
      checklist: candidateData.onboarding?.checklist || {},
      progress: candidateData.onboarding?.progress || 0,
      bgvStatus: candidateData.onboarding?.bgvStatus || "Not Started",
      createdDate: new Date().toISOString(),
      initiatedBy: hrName,
    };
  }

  if (scheduledDate) updatePayload.scheduledDate = scheduledDate;
  if (scheduledTime) updatePayload.scheduledTime = scheduledTime;
  if (meetingLink) updatePayload.meetingLink = meetingLink;
  if (offerDetails) updatePayload.offerDetails = offerDetails;

  await updateDoc(ref, updatePayload);

  await logRecruitmentActivity({
    actorId: hrId,
    actorName: hrName,
    actorRole: "hr",
    action: "HR_FINAL_DECISION_EXECUTED",
    entityType: "candidate",
    entityId: candidateId,
    entityName: candName,
    oldValue: oldStage,
    newValue: `HR Action: ${hrAction} | Remarks: ${hrRemarks}`,
    candidateId,
  });

  // Notify Admin
  try {
    await addDoc(collection(db, "notifications"), {
      recipientId: "admin",
      recipientRole: "admin",
      title: "HR Final Recruitment Action Completed",
      message: `HR ${hrName} executed final action "${hrAction}" for candidate ${candName}.`,
      link: "/admin/assigned-candidates",
      read: false,
      createdAt: serverTimestamp(),
    });
  } catch (_) {}

  // Notify Team Lead
  if (candidateData.assignedLeadId) {
    try {
      await addDoc(collection(db, "notifications"), {
        recipientId: candidateData.assignedLeadId,
        recipientRole: "lead",
        title: "HR Final Recruitment Action Completed",
        message: `HR ${hrName} executed final action "${hrAction}" for candidate ${candName}.`,
        link: "/lead/recruitment",
        read: false,
        createdAt: serverTimestamp(),
      });
    } catch (_) {}
  }
}

// ─── Interviews ────────────────────────────────────────────────────────────
export function useInterviews(candidateId = null) {
  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let q = candidateId
      ? query(collection(db, "interviews"), where("candidateId", "==", candidateId), orderBy("scheduledDate", "asc"))
      : query(collection(db, "interviews"), orderBy("scheduledDate", "asc"));
    const unsub = onSnapshot(q, (snap) => {
      setData(snap.docs.map((d) => ({ id: d.id, ...d.data() })));
      setLoading(false);
    }, (e) => { console.error("[useInterviews]", e); setLoading(false); });
    return unsub;
  }, [candidateId]);

  return { data, loading };
}

export async function deleteInterview(id, candidateId = null, deletedByName = "HR Admin") {
  let targetCandidateId = candidateId;
  let interviewData = null;

  try {
    const intSnap = await getDoc(doc(db, "interviews", id));
    if (intSnap.exists()) {
      interviewData = intSnap.data();
      if (!targetCandidateId) {
        targetCandidateId = interviewData.candidateId;
      }
    }
  } catch (e) {
    console.error("[deleteInterview] Fetch error:", e);
  }

  // Permanently delete interview document from Firestore
  await deleteDoc(doc(db, "interviews", id));

  // Reset Candidate profile & update recruitment stage back to "Shortlisted"
  if (targetCandidateId) {
    try {
      const candidateRef = doc(db, "candidates", targetCandidateId);
      await updateDoc(candidateRef, {
        stage: "Shortlisted",
        interviewScheduled: false,
        scheduledRound: "",
        scheduledDate: "",
        scheduledTime: "",
        scheduledDuration: "",
        meetingMode: "",
        meetingLink: "",
        specialInstructions: "",
        assignedLeadId: "",
        assignedLeadName: "",
        assignedLeadDept: "",
        updatedAt: serverTimestamp(),
      });
    } catch (e) {
      console.error("[deleteInterview] Candidate stage reset failed:", e);
    }
  }

  // Log recruitment activity
  await logRecruitmentActivity({
    actorId: "",
    actorName: deletedByName,
    actorRole: "hr",
    action: "INTERVIEW_DELETED",
    entityType: "interview",
    entityId: id,
    entityName: `Interview schedule for ${interviewData?.candidateName || "Candidate"}`,
    newValue: `Deleted interview schedule & candidate stage reverted back to Shortlisted`,
    candidateId: targetCandidateId,
  });
}

export async function cancelInterview(id, candidateId = null, reason = "Cancelled by HR", cancelledByName = "HR Admin") {
  let targetCandidateId = candidateId;
  let interviewData = null;

  try {
    const intSnap = await getDoc(doc(db, "interviews", id));
    if (intSnap.exists()) {
      interviewData = intSnap.data();
      if (!targetCandidateId) {
        targetCandidateId = interviewData.candidateId;
      }
    }
  } catch (e) {
    console.error("[cancelInterview] Fetch error:", e);
  }

  // Update interview status to Cancelled
  await updateDoc(doc(db, "interviews", id), {
    status: "Cancelled",
    cancellationReason: reason,
    cancelledAt: new Date().toISOString(),
    cancelledByName,
    updatedAt: serverTimestamp(),
  });

  // Reset Candidate profile & update recruitment stage back to "Shortlisted"
  if (targetCandidateId) {
    try {
      const candidateRef = doc(db, "candidates", targetCandidateId);
      await updateDoc(candidateRef, {
        stage: "Shortlisted",
        interviewScheduled: false,
        scheduledRound: "",
        scheduledDate: "",
        scheduledTime: "",
        scheduledDuration: "",
        meetingMode: "",
        meetingLink: "",
        specialInstructions: "",
        updatedAt: serverTimestamp(),
      });
    } catch (e) {
      console.error("[cancelInterview] Candidate stage reset failed:", e);
    }
  }

  // Log recruitment activity
  await logRecruitmentActivity({
    actorId: "",
    actorName: cancelledByName,
    actorRole: "hr",
    action: "INTERVIEW_CANCELLED",
    entityType: "interview",
    entityId: id,
    entityName: `Interview schedule for ${interviewData?.candidateName || "Candidate"}`,
    newValue: `Cancelled interview schedule & candidate stage reverted back to Shortlisted (${reason})`,
    candidateId: targetCandidateId,
  });
}


export async function createInterview(data) {
  // ─── Step 1: Validate all required fields ───────────────────────────────
  const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
  if (
    !data.candidateId ||
    !data.candidateEmail ||
    !emailRegex.test(data.candidateEmail) ||
    !data.round ||
    (!data.interviewerId && !data.interviewerName) ||
    !data.scheduledDate ||
    !data.scheduledTime ||
    !data.meetingMode ||
    !data.meetingLink ||
    data.meetingLink.trim() === ""
  ) {
    throw new Error("Validation Failed: Please complete all required fields before scheduling the interview.");
  }

  // ─── Step 2: Send invitation email BEFORE saving to Firestore ───────────
  // If this throws, the interview is never created (per user requirement).
  let emailQueueDocId = "";
  try {
    const emailResult = await sendInterviewInvitationEmail({
      candidateId:     data.candidateId,
      candidateName:   data.candidateName,
      candidateEmail:  data.candidateEmail,   // ← actual recipient from form
      jobTitle:        data.jobTitle,
      round:           data.round,
      scheduledDate:   data.scheduledDate,
      scheduledTime:   data.scheduledTime,
      duration:        data.duration,
      interviewerName: data.interviewerName,
      meetingMode:     data.meetingMode,
      meetingLink:     data.meetingLink,
      notes:           data.notes,
    });
    emailQueueDocId = emailResult.docId || "";
    console.log("[createInterview] Invitation email delivered to:", emailResult.recipient);
  } catch (emailErr) {
    // Re-throw the real error from emailService so the UI shows the actual reason
    console.error("[createInterview] Email sending failed — interview NOT created:", emailErr.message);
    throw emailErr;
  }

  // ─── Step 3: Save interview to Firestore (only if email succeeded) ───────
  const ref = await addDoc(collection(db, "interviews"), {
    ...data,
    status: "Scheduled",
    scheduledByRole: "hr",
    emailSent: true,
    emailStatus: "Sent",
    emailQueueDocId,
    emailSentAt: new Date().toISOString(),
    createdAt: serverTimestamp(),
  });

  // ─── Step 4: Sync candidate stage to "Interview Scheduled" ───────────────
  if (data.candidateId) {
    try {
      const candidateRef = doc(db, "candidates", data.candidateId);
      await updateDoc(candidateRef, {
        assignedLeadId: data.interviewerId || data.assignedLeadId || "",
        assignedLeadName: data.interviewerName || data.assignedLeadName || "",
        assignedLeadDept: data.interviewerDept || data.assignedLeadDept || "",
        stage: "Interview Scheduled",
        assignedAt: new Date().toISOString(),
        interviewScheduled: true,
        scheduledRound: data.round || "Technical Round 1",
        scheduledDate: data.scheduledDate || "",
        scheduledTime: data.scheduledTime || "",
        scheduledDuration: data.duration || "45 mins",
        meetingMode: data.meetingMode || "Google Meet",
        meetingLink: data.meetingLink || "",
        specialInstructions: data.notes || "",
        updatedAt: serverTimestamp(),
      });
    } catch (e) {
      console.error("[createInterview] Candidate stage sync failed:", e);
    }
  }

  // ─── Step 5: Create real-time notification for assigned Team Lead ─────────
  if (data.interviewerId || data.assignedLeadId) {
    try {
      await addDoc(collection(db, "notifications"), {
        recipientId: data.interviewerId || data.assignedLeadId,
        recipientRole: "lead",
        title: "New Candidate Assigned for Technical Interview",
        message: `Candidate ${data.candidateName || "Applicant"} for ${
          data.jobTitle || "Position"
        } has been assigned to you for ${data.round || "Interview"} on ${
          data.scheduledDate
        } at ${data.scheduledTime}.`,
        link: "/lead/recruitment",
        read: false,
        createdAt: serverTimestamp(),
      });
    } catch (e) {
      console.error("[createInterview] Notification creation failed:", e);
    }
  }

  // ─── Step 6: Log recruitment activity ────────────────────────────────────
  await logRecruitmentActivity({
    actorId: "",
    actorName: data.createdByName || "HR",
    actorRole: "hr",
    action: "INTERVIEW_SCHEDULED",
    entityType: "interview",
    entityId: ref.id,
    entityName: `Interview with ${data.candidateName || "Candidate"}`,
    newValue: `${data.round} scheduled & invitation email sent to ${data.candidateEmail}. Assigned to Team Lead ${data.interviewerName}.`,
    candidateId: data.candidateId,
    jobId: data.jobId,
  });

  return ref.id;
}

export async function submitInterviewFeedback(interviewId, feedback, candidateId = null) {
  await updateDoc(doc(db, "interviews", interviewId), {
    feedback,
    status: "Completed",
    completedAt: serverTimestamp(),
  });

  if (candidateId) {
    try {
      await updateDoc(doc(db, "candidates", candidateId), {
        latestInterviewFeedback: feedback,
        stage: feedback.recommendation === "Hire" ? "Interview Evaluation Complete" : "Manager Review",
        updatedAt: serverTimestamp(),
      });
    } catch (_) {}
  }
}

// ─── Recruitment Requests (from Team Leads) ────────────────────────────────
export function useRecruitmentRequests(status = null) {
  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let q = status
      ? query(collection(db, "recruitmentRequests"), where("status", "==", status), orderBy("createdAt", "desc"))
      : query(collection(db, "recruitmentRequests"), orderBy("createdAt", "desc"));
    const unsub = onSnapshot(q, (snap) => {
      setData(snap.docs.map((d) => ({ id: d.id, ...d.data() })));
      setLoading(false);
    }, (e) => { console.error("[useRecruitmentRequests]", e); setLoading(false); });
    return unsub;
  }, [status]);

  return { data, loading };
}

export function useMyRecruitmentRequests(teamLeadId) {
  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!teamLeadId) return;
    const q = query(collection(db, "recruitmentRequests"), where("requestedById", "==", teamLeadId), orderBy("createdAt", "desc"));
    const unsub = onSnapshot(q, (snap) => {
      setData(snap.docs.map((d) => ({ id: d.id, ...d.data() })));
      setLoading(false);
    }, (e) => { console.error("[useMyRecruitmentRequests]", e); setLoading(false); });
    return unsub;
  }, [teamLeadId]);

  return { data, loading };
}

export async function createRecruitmentRequest(data) {
  const ref = await addDoc(collection(db, "recruitmentRequests"), {
    ...data,
    status: "Submitted",
    createdAt: serverTimestamp(),
    updatedAt: serverTimestamp(),
  });
  return ref.id;
}

export async function approveRecruitmentRequest(requestId, approvedById, approvedByName) {
  await updateDoc(doc(db, "recruitmentRequests", requestId), {
    status: "Approved",
    approvedById,
    approvedByName,
    approvedAt: serverTimestamp(),
    updatedAt: serverTimestamp(),
  });
}

export async function rejectRecruitmentRequest(requestId, rejectedById, rejectedByName, reason) {
  await updateDoc(doc(db, "recruitmentRequests", requestId), {
    status: "Rejected",
    rejectedById,
    rejectedByName,
    rejectionReason: reason,
    updatedAt: serverTimestamp(),
  });
}

// ─── Offers ────────────────────────────────────────────────────────────────
export function useOffers(candidateId = null) {
  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let q = candidateId
      ? query(collection(db, "offers"), where("candidateId", "==", candidateId))
      : query(collection(db, "offers"), orderBy("createdAt", "desc"));
    const unsub = onSnapshot(q, (snap) => {
      setData(snap.docs.map((d) => ({ id: d.id, ...d.data() })));
      setLoading(false);
    }, (e) => { console.error("[useOffers]", e); setLoading(false); });
    return unsub;
  }, [candidateId]);

  return { data, loading };
}

export async function createOffer(data) {
  const ref = await addDoc(collection(db, "offers"), {
    ...data,
    status: "Sent",
    createdAt: serverTimestamp(),
  });
  return ref.id;
}

// ─── Activity Feed ─────────────────────────────────────────────────────────
export function useRecruitmentActivity(limitCount = 50) {
  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const q = query(collection(db, "recruitmentActivity"), orderBy("createdAt", "desc"), limit(limitCount));
    const unsub = onSnapshot(q, (snap) => {
      setData(snap.docs.map((d) => ({ id: d.id, ...d.data() })));
      setLoading(false);
    }, (e) => { console.error("[useRecruitmentActivity]", e); setLoading(false); });
    return unsub;
  }, [limitCount]);

  return { data, loading };
}

// ─── Referrals ─────────────────────────────────────────────────────────────
export function useReferrals(referredById = null) {
  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let q = referredById
      ? query(collection(db, "referrals"), where("referredById", "==", referredById), orderBy("createdAt", "desc"))
      : query(collection(db, "referrals"), orderBy("createdAt", "desc"));
    const unsub = onSnapshot(q, (snap) => {
      setData(snap.docs.map((d) => ({ id: d.id, ...d.data() })));
      setLoading(false);
    }, (e) => { console.error("[useReferrals]", e); setLoading(false); });
    return unsub;
  }, [referredById]);

  return { data, loading };
}

export async function createReferral(data) {
  const ref = await addDoc(collection(db, "referrals"), {
    ...data,
    status: "Submitted",
    createdAt: serverTimestamp(),
  });
  return ref.id;
}

// ─── Onboarding Candidates ─────────────────────────────────────────────────
// Returns all candidates whose recruitment stage indicates they have been
// moved to onboarding (HR final decision: "Move to Onboarding", "Hired",
// "Moved to Onboarding", or "Mark as Hired").
export function useOnboardingCandidates() {
  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const q = query(collection(db, "candidates"), orderBy("createdAt", "desc"));
    const unsub = onSnapshot(q, (snap) => {
      const filtered = snap.docs
        .map((d) => ({ id: d.id, ...d.data() }))
        .filter((c) => {
          if (c.archived) return false;

          const stage = (c.stage || "").toLowerCase();
          const hrAction = (c.hrFinalAction || "").toLowerCase();
          const status = (c.status || "").toLowerCase();

          // Exclude explicit rejects or holds
          if (stage.includes("reject") || stage.includes("hold") || status.includes("reject") || status.includes("hold")) {
            return false;
          }

          // Candidate MUST have been explicitly moved to onboarding by HR
          return (
            stage === "moved to onboarding" ||
            stage === "selected / moved to onboarding" ||
            stage === "hired" ||
            hrAction === "move to onboarding" ||
            hrAction === "mark as hired" ||
            status === "onboarding" ||
            status === "hired"
          );
        });
      setData(filtered);
      setLoading(false);
    }, (e) => { console.error("[useOnboardingCandidates]", e); setLoading(false); });
    return unsub;
  }, []);

  return { data, loading };
}

// Persist onboarding-specific fields (checklist, bgvStatus, progress) back to
// the candidate doc in Firestore under the `onboarding` sub-object.
export async function updateOnboardingRecord(candidateId, onboardingFields) {
  await updateDoc(doc(db, "candidates", candidateId), {
    onboarding: onboardingFields,
    updatedAt: serverTimestamp(),
  });
}
